import { Link, Navigate, useParams } from "react-router-dom";
import { Check, MapPin, ArrowRight } from "lucide-react";
import { CtaButton } from "@/components/site/CtaButton";
import { Eyebrow } from "@/components/site/Sections";
import { HOME_INSPECTIONS, AREAS, CITY_AREAS, MAP_ALL } from "@/data/homeSite";
import { AreasBlock, MeetAline } from "@/components/home/HomeSections";

export function InspectionsPage() {
  return (
    <div data-testid="inspections-page">
      <section className="border-b border-slate-200 bg-[#F5F3EC] py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Eyebrow>Inspection services & pricing</Eyebrow>
          <h1 className="mt-3 font-display text-4xl font-extrabold text-[#0B132B] sm:text-5xl">Pennsylvania inspections, clearly priced.</h1>
          <div className="mt-6 flex flex-wrap gap-2">{HOME_INSPECTIONS.map((s) => <a key={s.id} href={`#${s.id}`} className="rounded-full bg-white px-4 py-2 text-sm text-[#0B132B] hover:text-gold">{s.name}</a>)}</div>
        </div>
      </section>
      {HOME_INSPECTIONS.map((s, i) => (
        <section key={s.id} id={s.id} data-testid={`inspection-section-${s.id}`} className={`scroll-mt-24 py-16 ${i % 2 ? "bg-[#FAF8F1]" : ""}`}>
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[1.3fr_1fr] lg:px-8">
            <div>
              <h2 className="font-display text-3xl font-bold text-[#0B132B]">{s.name}</h2>
              <p className="mt-2 font-semibold text-gold">{s.price}</p>
              <p className="mt-4 leading-relaxed text-slate-600">{s.text}</p>
              <ul className="mt-6 grid gap-2 sm:grid-cols-2">{s.includes.map((x) => <li key={x} className="flex gap-2 text-sm text-[#0B132B]"><Check className="h-4 w-4 shrink-0 text-gold" /> {x}</li>)}</ul>
            </div>
            <div className="h-fit rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-xs uppercase tracking-widest text-slate-400">Turnaround</p><p className="mt-1 font-semibold text-[#0B132B]">{s.turnaround}</p>
              <p className="mt-4 text-xs uppercase tracking-widest text-slate-400">Who it's for</p><p className="mt-1 font-semibold text-[#0B132B]">{s.who}</p>
              <CtaButton to={s.href} className="mt-6 w-full justify-center" data-testid={`inspections-book-${s.id}`}>{s.cta}</CtaButton>
              {s.id === "occupancy-verification" && <Link to="/property-preservation" data-testid="inspections-preservation-link" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold">Need securing, winterization or trash-outs? Property preservation <ArrowRight className="h-4 w-4" /></Link>}
            </div>
          </div>
        </section>
      ))}
      <p className="mx-auto max-w-7xl px-4 pb-16 text-sm text-slate-500 lg:px-8">More services (preservation, mortgage field, commercial verification): <Link to="/field-services" className="font-semibold text-gold underline">Field services catalog</Link>.</p>
    </div>
  );
}

export function ServiceAreasPage() {
  return (
    <div data-testid="service-areas-page">
      <section className="bg-[#F5F3EC] py-16"><div className="mx-auto max-w-7xl px-4 lg:px-8">
        <Eyebrow>Service Areas</Eyebrow>
        <h1 className="mt-3 font-display text-4xl font-extrabold text-[#0B132B] sm:text-5xl">Service Areas in Pennsylvania</h1>
        <p className="mt-4 max-w-3xl text-slate-600">Two offices — Doylestown and Coal Township — covering Bucks and Montgomery counties, the Harrisburg area and Central PA.</p>
      </div></section>
      <AreasBlock />
    </div>
  );
}

export function AreaPage() {
  const { slug } = useParams();
  const a = AREAS.find((x) => x.slug === slug);
  if (!a) return <Navigate to="/service-areas" replace />;
  return (
    <div data-testid={`area-page-${a.slug}`}>
      <section className="bg-[#F5F3EC] py-16"><div className="mx-auto max-w-7xl px-4 lg:px-8">
        {a.parent && <Link to={`/service-areas/${a.parent}`} className="mb-3 block text-sm text-slate-500 hover:text-gold" data-testid="area-parent-link">← Central PA</Link>}
        <Eyebrow>{a.name}</Eyebrow>
        <h1 className="mt-3 font-display text-4xl font-extrabold text-[#0B132B] sm:text-5xl">{a.title}</h1>
        <p className="mt-4 max-w-3xl text-slate-600">{a.intro}</p>
        {a.offices.length > 0 && <div className="mt-4 space-y-1" data-testid="area-offices">{a.offices.map((o) => <p key={o} className="flex gap-2 text-sm text-slate-500"><MapPin className="h-4 w-4 shrink-0 text-gold" /> {o}</p>)}</div>}
        <div className="mt-8 flex flex-wrap gap-3"><CtaButton to="/property-inspections/book" size="lg">Book an Inspection</CtaButton><CtaButton to="/request-inspection" variant="slate" size="lg">Get a Quote</CtaButton></div>
      </div></section>
      <section className="py-16"><div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-2 lg:px-8">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#0B132B]">{a.parent ? "Nearby towns we serve" : "Counties & towns we serve"}</h2>
          <p className="mt-4 text-sm font-semibold text-[#0B132B]">{a.counties.join(", ")}</p>
          <div className="mt-4 flex flex-wrap gap-2">{a.towns.map((t) => <span key={t} className="rounded-full bg-[#F5F3EC] px-4 py-2 text-sm">{t}</span>)}</div>
          {!a.parent && a.slug === "central-pa" && <div className="mt-6 flex flex-wrap gap-2" data-testid="area-city-pages">{CITY_AREAS.map((c) => <Link key={c.slug} to={`/service-areas/${c.slug}`} className="inline-flex items-center gap-1 text-sm font-semibold text-gold underline">{c.name} inspections <ArrowRight className="h-3 w-3" /></Link>)}</div>}
          <h2 className="mt-10 font-display text-2xl font-bold text-[#0B132B]">Inspections available</h2>
          <ul className="mt-4 space-y-2">{HOME_INSPECTIONS.map((s) => <li key={s.id}><Link to={`/inspections#${s.id}`} className="inline-flex items-center gap-2 text-sm text-[#0B132B] hover:text-gold"><ArrowRight className="h-4 w-4 shrink-0 text-gold" /> {s.name} — {s.price}</Link></li>)}</ul>
          <p className="mt-6 text-sm text-slate-500">Not sure if we cover your address? Call (215) 445-4257 — we confirm before booking.</p>
        </div>
        <iframe title={`${a.name} map`} src={MAP_ALL} loading="lazy" className="h-[420px] w-full rounded-3xl border border-slate-200" />
      </div></section>
    </div>
  );
}

export function AboutPage() {
  return <div data-testid="about-page"><MeetAline heading="About Vertexhub Ventures" asH1 /></div>;
}

export function LegalPage({ title }) {
  return (
    <section className="mx-auto max-w-3xl px-4 py-20" data-testid="legal-page">
      <h1 className="font-display text-4xl font-bold text-[#0B132B]">{title}</h1>
      <p className="mt-6 text-slate-600">[{title} text to be provided by Vertexhub Ventures LLC.] Questions: hello@vhubagent.com · (215) 445-4257.</p>
    </section>
  );
}
